import { getRUM } from '@codametrix/client-observability';
import React, { useEffect, useId, useMemo, useRef } from 'react';
import {
  NoteHead,
  type RailTick,
  SectionRail,
  StreamTicks,
  useDocumentStreamNote
} from './document-stream';
import {
  arrangeByTemplate,
  type ResolvedNoteTemplate,
  type TemplateArrangement
} from './note-template';
import { SectionCard } from './section-card';
import { type SectionSlice, sliceSectionsFromBody } from './section-slicer';
import type { ViewAnnotation } from './cmx-document-content-adapter';
import { CmxDocumentContentBody } from './cmx-document-content-body';
import { MEDIA_TYPE_PLAIN } from './cmx-document-content-types';
import './styles/section-viewer.scss';

type SectionEntry = { slice: SectionSlice; title: string; id: string };

/** RUM action name for CDMX-20061's section-rendering performance AC. */
export const SECTION_RENDER_ACTION = 'cmx-document-content.sections.rendered';

export type SectionViewerProps = {
  /** The body Markdown. Absent for a `?body=reference` read. */
  body: string | null | undefined;
  /** Section annotations, adapted from a layer-4 read. */
  annotations: ViewAnnotation[];
  /**
   * Annotations to highlight within each section's content. Separate from
   * `annotations` because sections define the layout while spans decorate it;
   * empty until CDMX-20063.
   */
  spanAnnotations?: ViewAnnotation[];
  /** The body's media type (`body.mediaType`). Absent means Markdown. */
  mediaType?: string;
  /**
   * The note's template (CDMX-20060, `useNoteTemplate`). Applied only when the note's sections
   * carry a `section_code`; absent, or with untyped sections, the viewer keeps note order.
   */
  template?: ResolvedNoteTemplate | null;
  noteTitle?: string | null;
};

/**
 * Renders a CMX Document Content as section cards. This is the CDMX-20061 view.
 *
 * Order: the note's template order when a template resolved and the sections are typed (an L5
 * read), otherwise the order the sections appear in the note (every L4 read, which carries no
 * `section_code` -- CMXDC-LAYER-011). Where no payload exists the document is not sectionable and the
 * flat body is the correct fallback -- CDMX-20059's view -- rather than an error.
 */
export const SectionViewer: React.FC<SectionViewerProps> = ({
  body,
  annotations,
  spanAnnotations,
  mediaType,
  template,
  noteTitle
}) => {
  const idPrefix = useId();
  const streamNote = useDocumentStreamNote();
  // Timestamp taken before slicing so the reported duration covers the slice
  // plus React's render and commit, which is what a coder actually waits for.
  const startedAt = useRef(0);

  const result = useMemo(() => {
    startedAt.current =
      typeof performance !== 'undefined' ? performance.now() : 0;

    if (!body) {
      return null;
    }

    const sliced = sliceSectionsFromBody(body, annotations, {
      plain: mediaType === MEDIA_TYPE_PLAIN
    });
    const sliceMs =
      typeof performance !== 'undefined'
        ? performance.now() - startedAt.current
        : 0;

    return { ...sliced, sliceMs };
  }, [body, annotations, mediaType]);

  const sectionCount = result?.slices.length ?? 0;

  const arrangement = useMemo<TemplateArrangement | null>(() => {
    if (!template || !result?.slices.some((slice) => slice.sectionCode)) {
      return null;
    }
    return arrangeByTemplate(result.slices, template);
  }, [template, result]);

  // Ordered once, so the rail ticks and the cards cannot disagree. A slice's offset is unique and
  // does not move when the arrangement reorders, so an id stays on the same section across a
  // template load.
  const layout = useMemo(() => {
    if (!result) {
      return null;
    }
    const entry = (slice: SectionSlice, title: string): SectionEntry => ({
      slice,
      title,
      id: `${idPrefix}-section-${slice.start}`
    });
    if (arrangement && template) {
      return {
        placed: arrangement.placed.flatMap((placed) =>
          placed.slices.map((slice) => entry(slice, placed.label))
        ),
        unplaced: arrangement.unplaced.map((slice) =>
          entry(
            slice,
            (slice.sectionCode && template.sectionLabels[slice.sectionCode]) ||
              slice.title
          )
        )
      };
    }
    return {
      placed: result.slices.map((slice) => entry(slice, slice.title)),
      unplaced: []
    };
  }, [result, arrangement, template, idPrefix]);

  useEffect(() => {
    if (!result) {
      return;
    }

    const totalMs =
      typeof performance !== 'undefined'
        ? performance.now() - startedAt.current
        : 0;

    getRUM().addAction(SECTION_RENDER_ACTION, {
      sectionCount,
      annotationCount: annotations.length,
      unclaimedCount: result.unclaimed.length,
      bodyLength: body?.length ?? 0,
      sliceMs: Math.round(result.sliceMs * 100) / 100,
      totalMs: Math.round(totalMs * 100) / 100,
      templateKey: arrangement ? template?.template.templateKey ?? null : null
    });
  }, [result, sectionCount, annotations.length, body, arrangement, template]);

  if (!body) {
    return (
      <div className="cmx-document-content-section-viewer__empty">
        No document content.
      </div>
    );
  }

  if (!result) {
    return null;
  }

  const { preamble, slices, unclaimed } = result;

  const head = (meta: string) => (
    <NoteHead title={noteTitle} meta={meta} sticky={streamNote !== null} />
  );

  // No sections detected: the flat body is the honest rendering, not an error.
  if (slices.length === 0) {
    return (
      <div className="cmx-document-content-section-viewer">
        {head('No sections detected — showing the full document')}
        <div
          className={`cmx-document-content-section-viewer__content cmx-document-content-section-viewer__content--${
            streamNote ? 'in-stream' : 'flat'
          }`}
        >
          <CmxDocumentContentBody
            text={body}
            annotations={spanAnnotations}
            mediaType={mediaType}
          />
        </div>
      </div>
    );
  }

  const sectionsWord = slices.length === 1 ? 'section' : 'sections';
  const { placed, unplaced } = layout ?? { placed: [], unplaced: [] };
  const ticks: RailTick[] = [...placed, ...unplaced].map(({ id, title }) => ({
    id,
    label: title
  }));
  const card = ({ slice, title, id }: SectionEntry) => (
    <SectionCard
      key={`${slice.annotation.id}-${slice.start}`}
      anchorId={id}
      title={title}
      bodyMarkdown={slice.bodyMarkdown}
      annotations={spanAnnotations}
      mediaType={mediaType}
    />
  );

  const sections = (
    <>
      {placed.map(card)}
      {unplaced.length > 0 && template && (
        <>
          <div className="cmx-document-content-section-viewer__divider">
            Not in the {template.template.label} template ({unplaced.length})
          </div>
          {unplaced.map(card)}
        </>
      )}
    </>
  );

  const content = (
    <>
      {preamble && (
        <div className="cmx-document-content-section-viewer__preamble">
          <CmxDocumentContentBody
            text={preamble}
            annotations={spanAnnotations}
            mediaType={mediaType}
          />
        </div>
      )}

      {sections}

      {unclaimed.length > 0 && (
        <>
          <div className="cmx-document-content-section-viewer__divider">
            Outside any section ({unclaimed.length})
          </div>
          {unclaimed.map((gap) => (
            <div
              className="cmx-document-content-section-viewer__unclaimed"
              key={`unclaimed-${gap.start}`}
            >
              <CmxDocumentContentBody
                text={gap.text}
                annotations={spanAnnotations}
                mediaType={mediaType}
              />
            </div>
          ))}
        </>
      )}
    </>
  );

  return (
    <div className="cmx-document-content-section-viewer">
      {head(
        arrangement && template
          ? `${slices.length} ${sectionsWord} · ${template.template.label} template`
          : `${slices.length} ${sectionsWord} · in note order`
      )}

      {streamNote ? (
        <>
          <StreamTicks ticks={ticks} />
          <div className="cmx-document-content-section-viewer__content cmx-document-content-section-viewer__content--in-stream">
            {content}
          </div>
        </>
      ) : (
        <div className="cmx-document-content-section-viewer__frame">
          <SectionRail
            groups={[{ key: '', label: '', anchorId: null, ticks }]}
          />
          <div className="cmx-document-content-section-viewer__content">
            {content}
            <div
              className="cmx-document-content-section-viewer__end-spacer"
              aria-hidden
            />
          </div>
        </div>
      )}
    </div>
  );
};
